// PDP add-on list (Blanc Space custom)
// - Up to max-visible eligible rows at once: the design's matching grip first
//   (server-rendered from blancspace.matching_accessories), then the configured
//   products in order. Products already in the cart are skipped.
// - Nothing is preselected (UK reg. 40 / CMA: a paid extra needs the shopper's
//   own tick).
// - Rows with data-requires-case-type (MagSafe accessories) show only while that
//   case type is selected; unticked if the shopper switches away from it.
// - Products with a Phone Model option follow the main case selection; the
//   matching grip's colour follows the bumper (Black → Black, Clear → White).
// - Ticked items are added in one request before the main product, so Impact's
//   confirmation carousel and cart state contain everything together.

(() => {
	if (window.__upsellProtectorInitialized) return;
	window.__upsellProtectorInitialized = true;

	// Shared with custom.js: product ids the shopper removed/unticked this
	// session. Kept so the cart-side offers (smart upsell, added sheet) can skip
	// what the shopper already turned down; the list itself never preselects.
	const UPSELL_REMOVED_KEY = 'blancspace.upsellRemoved';
	const readRemovedUpsells = () => {
		try { return new Set((JSON.parse(sessionStorage.getItem(UPSELL_REMOVED_KEY) || '[]') || []).map(String)); } catch (error) { return new Set(); }
	};
	const writeRemovedUpsells = (set) => {
		try { sessionStorage.setItem(UPSELL_REMOVED_KEY, JSON.stringify([...set])); } catch (error) { /* storage unavailable */ }
	};
	const track = (name, props) => {
		try { window.bsTrack?.(name, props); } catch (error) { /* analytics must never break shopping */ }
	};

	// Grip ring colours are White/Black/Pink/Blue; the bumper only picks between two.
	const GRIP_COLOUR_FOR_BUMPER = { black: 'Black', clear: 'White' };

	class UpsellProtector extends HTMLElement {
		connectedCallback() {
			if (this._connected) return;
			this._connected = true;
			this.abortController = new AbortController();
			const signal = this.abortController.signal;
			this.items = [...this.querySelectorAll('[data-upsell-item]')];
			this.formId = this.getAttribute('form-id');
			this.form = document.forms[this.formId];
			this.phoneIndex = parseInt(this.getAttribute('phone-position'), 10) - 1;
			this.caseIndex = parseInt(this.getAttribute('case-position'), 10) - 1;
			this.colourIndex = parseInt(this.getAttribute('colour-position'), 10) - 1;
			this.currentModel = this.getAttribute('current-model') || '';
			this.currentCaseType = this.getAttribute('current-case-type') || '';
			this.currentColour = this.getAttribute('current-colour') || '';
			this.maxVisible = Math.max(1, parseInt(this.getAttribute('max-visible'), 10) || 3);
			this.maxTotal = Math.max(this.maxVisible, parseInt(this.getAttribute('max-total'), 10) || this.maxVisible);
			// Expands once and stays open: nothing the shopper ticked can hide behind a fold.
			this.expanded = false;
			this.moreButton = this.querySelector('[data-upsell-more]');
			this.moreButton?.addEventListener('click', () => {
				this.expanded = true;
				track('pdp_addons_expanded', { case_type: this.currentCaseType });
				this._render();
			}, { signal });
			this.cartProductIds = new Set(
				(this.getAttribute('cart-product-ids') || '').split(',').map((id) => id.trim()).filter(Boolean)
			);
			this._adding = false;
			this._cartRequest = 0;

			this.items.forEach((item) => {
				const select = item.querySelector('[data-upsell-select]');
				const checkbox = item.querySelector('.upsell-protector__checkbox');
				select?.addEventListener('change', () => {
					item.dataset.colourChosen = 'true';
					this._syncPrice(item);
				}, { signal });

				// The whole row is the tap target on phones; the variant chip keeps its own tap.
				item.addEventListener('click', (event) => {
					if (!checkbox || event.target.closest('select, input, label, [data-bs-cart-zoom]')) return;
					checkbox.checked = !checkbox.checked;
					checkbox.dispatchEvent(new Event('change', { bubbles: true }));
				}, { signal });

				checkbox?.addEventListener('change', (event) => {
					const set = readRemovedUpsells();
					const productId = String(item.dataset.productId);
					if (event.target.checked) set.delete(productId);
					else set.add(productId);
					writeRemovedUpsells(set);
					item.classList.toggle('is-checked', event.target.checked);
					track('pdp_addon_toggled', {
						handle: item.dataset.handle,
						slot: item.dataset.slot || 'configured',
						match: item.dataset.match || null,
						position: this._visibleItems().indexOf(item) + 1,
						checked: event.target.checked,
						case_type: this.currentCaseType,
					});
				}, { signal });
			});

			document.addEventListener('variant:change', (event) => {
				const variant = event.detail?.variant;
				if (!variant?.options || event.target !== this.form) return;
				this.currentModel = variant.options[this.phoneIndex] || '';
				this.currentCaseType = variant.options[this.caseIndex] || '';
				this.currentColour = variant.options[this.colourIndex] || '';
				this._syncCandidates();
			}, { signal });

			document.addEventListener('cart:change', (event) => {
				if (event.detail?.cart) this._applyCart(event.detail.cart);
			}, { signal });
			document.addEventListener('cart:refresh', () => this._refreshCart(), { signal });
			window.addEventListener('pageshow', (event) => {
				if (event.persisted) this._refreshCart();
			}, { signal });

			if (this.form) {
				// Intercept at document capture: ancestor capture listeners always
				// run before at-target listeners, so this reliably precedes
				// Impact's ProductForm._onSubmit regardless of registration order.
				document.addEventListener('submit', (event) => {
					if (event.target === this.form) this._submitUpsell(event);
				}, { capture: true, signal });

				// One persistent injector tied to THIS form's add event. Pending items
				// are armed by _submitUpsell and consumed here exactly once; if the
				// main submit never completes, they expire instead of leaking into an
				// unrelated later add.
				document.addEventListener('variant:add', (addEvent) => {
					const pending = this._pendingUpsellItems;
					if (!pending || addEvent.target !== this.form) return;
					this._pendingUpsellItems = null;
					if (Date.now() - pending.time > 15000) return;
					const existingItems = Array.isArray(addEvent.detail?.items) ? addEvent.detail.items : [];
					addEvent.detail.items = [...existingItems, ...pending.items];
				}, { capture: true, signal });
			}

			this._syncCandidates();
		}

		disconnectedCallback() {
			this.abortController?.abort();
			this._connected = false;
		}

		_syncCandidates() {
			this.items.forEach((item) => {
				const select = item.querySelector('[data-upsell-select]');
				const required = item.dataset.requiresCaseType;
				if (!select || (required && required !== this.currentCaseType)) {
					item.dataset.eligible = 'false';
					return;
				}

				let option;
				if (item.dataset.modelMatched === 'true') {
					option = [...select.options].find((candidate) => candidate.dataset.model === this.currentModel && !candidate.disabled);
				} else {
					const wanted = item.dataset.slot && item.dataset.colourChosen !== 'true'
						? GRIP_COLOUR_FOR_BUMPER[this.currentColour.toLowerCase()]
						: null;
					if (wanted) option = [...select.options].find((candidate) => candidate.textContent.trim() === wanted && !candidate.disabled);
					option ||= select.selectedOptions[0]?.disabled ? null : select.selectedOptions[0];
					option ||= [...select.options].find((candidate) => !candidate.disabled);
				}

				item.dataset.eligible = String(Boolean(option));
				if (option) {
					select.value = option.value;
					this._syncPrice(item);
				}
			});

			this._render();
		}

		_visibleItems() {
			return this.items.filter((item) => !item.hidden);
		}

		_render() {
			const candidates = this.items.filter(
				(item) => item.dataset.eligible === 'true' && !this.cartProductIds.has(item.dataset.productId)
			);
			const listed = candidates.slice(0, this.maxTotal);
			// A ticked row never folds away: a case-type switch can push it past the fold
			// (the grip row enters at the top), and hiding it would silently untick it.
			const ticked = (item) => item.querySelector('.upsell-protector__checkbox')?.checked;
			const visible = new Set(this.expanded ? listed : listed.filter((item, index) => index < this.maxVisible || ticked(item)));
			const extra = listed.length - visible.size;
			if (this.moreButton) {
				this.moreButton.hidden = extra <= 0;
				const label = this.moreButton.querySelector('[data-upsell-more-label]');
				if (label) label.textContent = `Show ${extra} more`;
			}

			this.items.forEach((item) => {
				const show = visible.has(item);
				item.hidden = !show;
				const checkbox = item.querySelector('.upsell-protector__checkbox');
				// A row that disappears (case type changed, item already in cart)
				// must not ride along with the next Add to cart unseen.
				if (!show && checkbox?.checked) {
					checkbox.checked = false;
					item.classList.remove('is-checked');
				}
			});
			this.classList.toggle('is-hidden', !visible.size);
			// The per-item nudge reads well alone and clutters a list.
			this.classList.toggle('is-list', visible.size > 1);

			const signature = [...visible].map((item) => item.dataset.handle).join(',');
			if (visible.size && signature !== this._impressionSignature) {
				this._impressionSignature = signature;
				track('pdp_addons_shown', {
					handles: [...visible].map((item) => item.dataset.handle),
					matching: [...visible].find((item) => item.dataset.slot)?.dataset.match || null,
					case_type: this.currentCaseType,
				});
			}
		}

		_syncPrice(item) {
			const select = item.querySelector('[data-upsell-select]');
			const selected = select?.selectedOptions[0];
			const price = item.querySelector('[data-upsell-price]');

			if (selected?.dataset.price && price) price.textContent = selected.dataset.price;
			// Variants without their own photo keep whatever is showing.
			const image = item.querySelector('[data-upsell-image]');
			if (selected?.dataset.image && image && image.getAttribute('src') !== selected.dataset.image) {
				image.src = selected.dataset.image;
				image.closest('[data-bs-cart-zoom]')?.setAttribute('data-bs-zoom-src', selected.dataset.zoom || selected.dataset.image);
			}
		}

		_applyCart(cart) {
			this.cartProductIds = new Set((cart.items || []).map((item) => String(item.product_id)));
			this._render();
		}

		async _refreshCart() {
			const request = ++this._cartRequest;
			try {
				const root = window.Shopify?.routes?.root || '/';
				const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
				if (!response.ok || request !== this._cartRequest) return;
				this._applyCart(await response.json());
			} catch (error) {
				// Keep the server-rendered cart snapshot when refresh is unavailable.
			}
		}

		async _submitUpsell(event) {
			// Our own requestSubmit() below re-enters this document-capture
			// handler synchronously; let that submit through to Impact untouched.
			if (this._passthrough) return;

			const isCustomilyForm = Boolean(this.form.querySelector('[data-customily-personalize-trigger]'));
			// While Customily is not ready its readiness blocker (registered on
			// the form) must keep swallowing submits; our document-capture
			// intercept runs earlier, so step aside until the trigger exists.
			if (isCustomilyForm && !this.form.querySelector('#customily-personalize-button')) return;

			// A tap during the pre-add window must not reach Impact's handler:
			// it would start an independent add and duplicate the main product.
			if (this._adding) {
				event.preventDefault();
				event.stopImmediatePropagation();
				return;
			}

			const chosen = this._visibleItems()
				.filter((item) => item.querySelector('.upsell-protector__checkbox')?.checked)
				.map((item) => ({ item, id: parseInt(item.querySelector('[data-upsell-select]')?.value, 10) }))
				.filter((entry) => entry.id);
			if (!chosen.length || this.classList.contains('is-hidden')) return;

			event.preventDefault();
			event.stopImmediatePropagation();
			this._adding = true;

			// Impact's own busy state only starts once its handler runs; mark the
			// CTAs busy during the add-ons' pre-add fetch so a double-tap has no
			// enabled target.
			const submitButtons = [...this.form.elements].filter(
				(el) => el.tagName === 'BUTTON' && el.type === 'submit' && !el.disabled
			);
			submitButtons.forEach((button) => {
				button.setAttribute('aria-busy', 'true');
				button.disabled = true;
			});

			try {
				const root = window.Shopify?.routes?.root || '/';
				// sliderCartOpen=false: Customily's store-level requestSkipping hook
				// ignores this request, so it is not treated as a new personalisation.
				const response = await fetch(`${root}cart/add.js${isCustomilyForm ? '?sliderCartOpen=false' : ''}`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
					body: JSON.stringify({ items: chosen.map((entry) => ({ id: entry.id, quantity: 1 })) }),
				});
				if (response.ok) {
					const added = (await response.json()).items || [];
					// bs_source lets PostHog's add_to_cart tell list take-up apart from other adds.
					added.forEach((line) => {
						const row = chosen.find((entry) => String(entry.item.dataset.productId) === String(line.product_id))?.item;
						line.bs_source = row?.dataset.slot ? `pdp_addon_${row.dataset.match || 'match'}` : 'pdp_addon';
					});
					this._pendingUpsellItems = { items: added, time: Date.now() };
				}
			} catch (error) {
				// The customer's main product must still proceed if an add-on fails.
			}

			submitButtons.forEach((button) => {
				button.removeAttribute('aria-busy');
				button.disabled = false;
			});
			this._passthrough = true;
			try {
				this.form.requestSubmit();
			} finally {
				this._passthrough = false;
			}
			window.setTimeout(() => {
				this._adding = false;
			}, 500);
		}
	}

	if (!customElements.get('upsell-protector')) {
		customElements.define('upsell-protector', UpsellProtector);
	}
})();
